// Email validation regex (RFC 5322 compliant)
export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// Phone number regex (supports various international formats)
export const PHONE_REGEX = /^[+]?[1-9][\d]{0,15}$/;

// More flexible phone regex for display (allows spaces, dashes, parentheses)
export const PHONE_DISPLAY_REGEX = /^[+]?[(]?[\d\s\-()]{10,20}$/;

// Phone input may contain formatting, but never alphabetic characters.
export const PHONE_ALLOWED_CHARS_REGEX = /^\+?[\d\s\-()]+$/;

// Password strength regex
export const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;

// Name validation (letters, spaces, hyphens, apostrophes)
export const NAME_REGEX = /^[a-zA-Z\s\-']+$/;

// Company name validation (letters, numbers, spaces, common business characters)
export const COMPANY_NAME_REGEX = /^[a-zA-Z0-9\s\-&.,()]+$/;

// Website URL validation
export const WEBSITE_REGEX =
  /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

// Common personal email domains that should be restricted for business signups
export const PERSONAL_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "mail.com",
  "protonmail.com",
  "tutanota.com",
  "zoho.com",
  "yandex.com",
  "mail.ru",
  "qq.com",
  "163.com",
  "126.com",
  "sina.com",
  "live.com",
  "msn.com",
  "comcast.net",
  "verizon.net",
  "att.net",
  "sbcglobal.net",
  "cox.net",
  "charter.net",
  "earthlink.net",
  "juno.com",
  "netzero.net",
  "rediffmail.com",
  "fastmail.com",
  "gmx.com",
  "web.de",
  "t-online.de",
  "freenet.de",
];

export interface ValidationResult {
  isValid: boolean;
  message: string;
}

// export const validateEmail = (email: string, allowPersonalDomains: boolean = true): ValidationResult => {
//   if (!email) {
//     return { isValid: false, message: 'Email is required' };
//   }

//   if (!EMAIL_REGEX.test(email)) {
//     return { isValid: false, message: 'Please enter a valid email address' };
//   }
//   return { isValid: true, message: '' };
//   if (email.length > 254) {
//     return { isValid: false, message: 'Email address is too long' };
//   }

//   // Check for business email domain restriction
//   if (!allowPersonalDomains) {
//     const domain = email.split('@')[1]?.toLowerCase();
//     if (domain && PERSONAL_EMAIL_DOMAINS.includes(domain)) {
//       return {
//         isValid: false,
//         message: 'Please use a business email address. Personal email domains (Gmail, Yahoo, etc.) are not allowed for company registration.'
//       };
//     }
//   }

//   return { isValid: true, message: '' };
// };
export const validateEmail = (
  email: string,
  allowPersonalDomains: boolean = true
): ValidationResult => {
  if (!email.trim()) {
    return { isValid: false, message: "Email is required" };
  }

  if (email.length > 254) {
    return { isValid: false, message: "Email address is too long" };
  }

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!EMAIL_REGEX.test(email)) {
    return {
      isValid: false,
      message: "Enter valid email (example: abc@g.com)",
    };
  }

  // Business email check
  if (!allowPersonalDomains) {
    const domain = email.split("@")[1]?.toLowerCase();

    if (domain && PERSONAL_EMAIL_DOMAINS.includes(domain)) {
      return {
        isValid: false,
        message:
          "Please use a business email address. Personal emails are not allowed",
      };
    }
  }

  return { isValid: true, message: "" };
};
export const validateBusinessEmail = (email: string): ValidationResult => {
  return validateEmail(email, false);
};

export const validatePhone = (
  phone: string,
  required: boolean = false
): ValidationResult => {
  if (!phone) {
    return required
      ? { isValid: false, message: "Phone number is required" }
      : { isValid: true, message: "" };
  }

  // Remove all non-digit characters for validation
  const cleanPhone = phone.replace(/\D/g, "");

  if (cleanPhone.length < 10) {
    return {
      isValid: false,
      message: "Phone number must be at least 10 digits",
    };
  }

  if (cleanPhone.length > 15) {
    return { isValid: false, message: "Phone number is too long" };
  }

  if (!PHONE_DISPLAY_REGEX.test(phone)) {
    return { isValid: false, message: "Please enter a valid phone number" };
  }

  return { isValid: true, message: "" };
};

export const validatePassword = (password: string): ValidationResult => {
  if (!password) {
    return { isValid: false, message: "Password is required" };
  }

  if (password.length < 8) {
    return {
      isValid: false,
      message: "Password must be at least 8 characters long",
    };
  }

  if (password.length > 128) {
    return { isValid: false, message: "Password is too long" };
  }

  if (!/(?=.*[a-z])/.test(password)) {
    return {
      isValid: false,
      message: "Password must contain at least one lowercase letter",
    };
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    return {
      isValid: false,
      message: "Password must contain at least one uppercase letter",
    };
  }

  if (!/(?=.*\d)/.test(password)) {
    return {
      isValid: false,
      message: "Password must contain at least one number",
    };
  }
  if (!/(?=.*[!@#$%^&*(),.?":{}|<>_\-\\[\]\\\/+=~`])/.test(password)) {
    return {
      isValid: false,
      message: "Password must contain at least one special character",
    };
  }
  return { isValid: true, message: "" };
};

export const validateName = (
  name: string,
  fieldName: string
): ValidationResult => {
  if (!name) {
    return { isValid: false, message: `${fieldName} is required` };
  }

  if (name.length < 2) {
    return {
      isValid: false,
      message: `${fieldName} must be at least 2 characters long`,
    };
  }

  if (name.length > 50) {
    return { isValid: false, message: `${fieldName} is too long` };
  }

  if (!NAME_REGEX.test(name)) {
    return {
      isValid: false,
      message: `${fieldName} contains invalid characters`,
    };
  }

  return { isValid: true, message: "" };
};

export const validateCompanyName = (name: string): ValidationResult => {
  if (!name) {
    return { isValid: false, message: "Company name is required" };
  }

  if (name.length < 2) {
    return {
      isValid: false,
      message: "Company name must be at least 2 characters long",
    };
  }

  if (name.length > 100) {
    return { isValid: false, message: "Company name is too long" };
  }

  if (!COMPANY_NAME_REGEX.test(name)) {
    return {
      isValid: false,
      message: "Company name contains invalid characters",
    };
  }

  return { isValid: true, message: "" };
};

export const validateWebsite = (website: string): ValidationResult => {
  if (!website) {
    return { isValid: true, message: "" }; // Website is optional
  }

  // Add https:// if no protocol specified
  const urlToTest =
    website.startsWith("http://") || website.startsWith("https://")
      ? website
      : `https://${website}`;

  if (!WEBSITE_REGEX.test(urlToTest)) {
    return { isValid: false, message: "Please enter a valid website URL" };
  }

  return { isValid: true, message: "" };
};

export const validateConfirmPassword = (
  password: string,
  confirmPassword: string
): ValidationResult => {
  if (!confirmPassword) {
    return { isValid: false, message: "Please confirm your password" };
  }

  if (password !== confirmPassword) {
    return { isValid: false, message: "Passwords do not match" };
  }

  return { isValid: true, message: "" };
};

// Format phone number for display (US format)
export const formatPhoneNumber = (phone: string): string => {
  const cleanPhone = phone.replace(/\D/g, "");

  if (cleanPhone.length === 10) {
    return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(
      3,
      6
    )}-${cleanPhone.slice(6)}`;
  }

  if (cleanPhone.length === 11 && cleanPhone.startsWith("1")) {
    return `+1 (${cleanPhone.slice(1, 4)}) ${cleanPhone.slice(
      4,
      7
    )}-${cleanPhone.slice(7)}`;
  }

  return phone; // Return original if can't format
};

export const formatNumericPhoneInput = (
  phone: string,
  phoneCode = ""
): string => {
  const digitsOnly = phone.replace(/\D/g, "");
  const phoneCodeDigits = phoneCode.replace(/\D/g, "");

  if (!phoneCode) {
    return digitsOnly;
  }

  const localDigits =
    phoneCodeDigits && digitsOnly.startsWith(phoneCodeDigits)
      ? digitsOnly.slice(phoneCodeDigits.length)
      : digitsOnly;

  return localDigits ? `${phoneCode} ${localDigits}` : phoneCode;
};

// Real-time validation for form fields
export const getFieldValidation = (
  fieldName: string,
  value: string,
  additionalValue?: string,
  allowPersonalEmail: boolean = true
): ValidationResult => {
  switch (fieldName) {
    case "email":
      return validateEmail(value, allowPersonalEmail);
    case "business_email":
      return validateBusinessEmail(value);
    case "phone":
      return validatePhone(value, false);
    case "password":
      return validatePassword(value);
    case "confirm_password":
      return validateConfirmPassword(additionalValue || "", value);
    case "first_name":
      return validateName(value, "First name");
    case "last_name":
      return validateName(value, "Last name");
    case "company_name":
      return validateCompanyName(value);
    case "company_website":
      return validateWebsite(value);
    default:
      return { isValid: true, message: "" };
  }
};

// Check if user is super admin (can be used to bypass business email restrictions)
export const isUserSuperAdmin = (user: any): boolean => {
  return user?.is_super_admin === true;
};

// Validate email with super admin override capability
export const validateEmailWithSuperAdminOverride = (
  email: string,
  currentUser: any = null,
  forceBusinessEmail: boolean = false
): ValidationResult => {
  // If user is super admin and not forcing business email, allow personal domains
  if (currentUser && isUserSuperAdmin(currentUser) && !forceBusinessEmail) {
    return validateEmail(email, true); // Allow personal domains for super admin
  }

  // For regular company signup, enforce business email
  return validateBusinessEmail(email);
};

// utils/validation.ts

const formatFieldName = (field: string): string => {
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const validateField = (field: string, value: any): string => {
  switch (field) {
    case "first_name":
    case "last_name":
      if (!value || value.trim() === "") {
        return `${formatFieldName(field)} is required`;
      }
      if (value.length < 2) {
        return `${formatFieldName(field)} must be at least 2 characters`;
      }
      if (value.length > 50) {
        return `${formatFieldName(field)} must be less than 50 characters`;
      }
      if (!/^[a-zA-Z\s'-]+$/.test(value)) {
        return `${formatFieldName(
          field
        )} can only contain letters, spaces, hyphens, and apostrophes`;
      }
      break;

    case "phone":
      if (value) {
        if (!PHONE_ALLOWED_CHARS_REGEX.test(value)) {
          return "Phone number can only contain numbers, spaces, +, -, and parentheses";
        }

        const cleaned = value.replace(/\D/g, "");
        const phonePattern = /^\d{10}$/;

        if (!phonePattern.test(cleaned)) {
          return "Please enter a valid 10-digit phone number";
        }
      }
      break;

    case "location":
      if (!value || value.trim() === "") {
        return "Location is required";
      }
      if (value.length > 100) {
        return "Location must be less than 100 characters";
      }
      break;

    case "professional_summary":
      if (!value || value.trim() === "") {
        return "Professional Summary is required";
      }
      if (value.trim().length < 100) {
        return "Professional Summary must be at least 100 characters";
      }
      if (value.length > 1000) {
        return "Professional Summary must be less than 1000 characters";
      }
      break;

    case "current_job_title":
      if (value && value.length > 100) {
        return "Job Title must be less than 100 characters";
      }
      break;

    case "current_company":
      if (value && value.length > 100) {
        return "Company Name must be less than 100 characters";
      }
      break;

    case "years_of_experience":
      if (value && (value < 0 || value > 70)) {
        return "Years of Experience must be between 0 and 70";
      }
      break;

    case "linkedin_url":
    case "github_url":
    case "portfolio_url":
    case "personal_website":
      if (value) {
        try {
          new URL(value);
          if (!/^https?:\/\//i.test(value)) {
            return "URL must start with http:// or https://";
          }
        } catch {
          return "Please enter a valid URL";
        }
      }
      break;

    default:
      break;
  }

  return "";
};
